import type { TZDate } from '@date-fns/tz';
import { TZDateMini } from '@date-fns/tz';
import { addMilliseconds } from 'date-fns/addMilliseconds';
import { differenceInMilliseconds } from 'date-fns/differenceInMilliseconds';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { interval } from 'date-fns/interval';
import { useMemo } from 'react';
import { useCounter, useInterval } from 'usehooks-ts';

import { getUserPermsSync } from '@/rbac';
import type { PermissionAction, PermissionName } from '@models';
import routeActions from '@routes/actions';
import type { AppAsyncThunk, RootState } from '@store';
import { useAppDispatch, useAppSelector, useAppStore } from '@store/hooks';
import { fetchFeeds } from '@features/system-health/feeds.slice';
import { fetchServices } from '@features/system-health/services.slice';
import useIsAuthenticated from '@hooks/use-is-authenticated';

interface BackgroundServiceConfig {
  /**
   * Actions required to access this service
   */
  actions: PermissionAction[];
  /**
   * The service thunk to be dispatched on each cycle
   *
   * TODO: Gracefully extend later if support for thunk args is needed
   */
  thunk: AppAsyncThunk<any, void>;
  /**
   * A selector to determine if the service is already fetching
   */
  isFetching: (state: RootState) => boolean;
  /**
   * Optional permissions required to access this service
   */
  permissions?: PermissionName[];
}

// Details about the services we want to add to our background cycler. If an
// entry doesn't have a `permissions` key it is only used in admin accounts.
const SERVICE_LIST: BackgroundServiceConfig[] = [
  {
    actions: routeActions.system,
    thunk: fetchServices,
    isFetching: state => state.services.status === 'loading',
  },
  {
    actions: routeActions.system,
    thunk: fetchFeeds,
    isFetching: state => state.feeds.status === 'loading',
  },
];

const BG_CYCLE_ELAPSE = 300000; // Time (in ms) of each service fetch cycle

export interface ServiceFetchCycler {
  cycleCount: number;
  delay: number;
  getRemainingLabel: () => string;
  getRemainingMilliseconds: () => number;
  lastRun: TZDate;
  nextRun: TZDate;
}

export const useServiceFetchCycler = (
  delay = BG_CYCLE_ELAPSE,
): ServiceFetchCycler => {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const isAuthenticated = useIsAuthenticated();
  const account = useAppSelector(state => state.auth.account);
  const isEngineHealthy = useAppSelector(
    state => state.app.healthCheck.isEngineHealthy,
  );
  const isLicenseGood = useAppSelector(
    state => state.app.healthCheck.isLicenseGood,
  );

  const cycleCounter = useCounter();

  const currentInterval = useMemo(() => {
    const start = new TZDateMini();
    const end = addMilliseconds(start, delay);

    return interval(start, end);
  }, [cycleCounter.count]);

  const getRemainingLabel = () => formatDistanceToNow(currentInterval.end);

  const getRemainingMilliseconds = () =>
    differenceInMilliseconds(currentInterval.end, new TZDateMini());

  useInterval(() => {
    if (
      isAuthenticated &&
      isEngineHealthy &&
      isLicenseGood &&
      isLicenseGood.isBeforeExpiration
    ) {
      Promise.all(
        SERVICE_LIST.filter(({ actions, isFetching, permissions }) => {
          // Skip services that are already fetching
          if (isFetching(store.getState())) return false;

          // If no permissions are set, check for admin status
          const isAdmin = !permissions && account && account.isAdmin;

          let isPermitted = false;

          if (!isAdmin) {
            if (
              !permissions ||
              (permissions instanceof Array && !permissions.length)
            ) {
              // If the service requires no permissions, open the gate...
              isPermitted = true;
            } else {
              // If there are required permissions then check them against
              // the action list
              const userPermsList = getUserPermsSync({
                account,
                actions,
              });
              // Every permission must be present amongst the user permissions
              // for this service item
              isPermitted =
                userPermsList instanceof Error
                  ? false
                  : permissions?.every(permission => userPermsList[permission]);
            }
          }

          return isAdmin || isPermitted;
        }).map(({ thunk }) => dispatch(thunk())),
      ).catch(console.error);

      cycleCounter.increment();
    }
  }, delay);

  return {
    delay,
    cycleCount: cycleCounter.count,
    lastRun: currentInterval.start,
    nextRun: currentInterval.end,
    getRemainingLabel,
    getRemainingMilliseconds,
  };
};

export default useServiceFetchCycler;
